import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { hasScope } from '@nyce/admin-kit/api';
import { Button, Chip, Switch, Tooltip } from '@nyce/admin-kit/ui';
import {
  ActionsRow,
  EmptyState,
  ErrorBox,
  Loading,
  Page,
  SortSelect,
  SortTh,
  TableScroller,
  useNotice,
  useSort,
  type SortKeys,
} from '../../components/common';
import { useParam } from '../statistics/shared';
import { EnvPicker, useClusterEnvironments } from '../../components/envPicker';
import { SCOPES } from '../../api';
import { pods as podsApi, type Deployment, type Pod, type PodList } from '../../api/pods';
import { logsLink } from '../../lib/logFilters';
import { byNamespace, deploymentProblems, formatAge, podStatusTone, shortImage } from '../../lib/pods';
import type { HubLogin } from '../../session';
import { errorMessage } from '../../lib/errors';
import { ActionDialog, type PendingAction } from './ActionDialog';
import { PodEventsDialog } from './PodEvents';
import { podLogsPath } from './PodLogsPage';
import { PodMetricsDialog } from './PodMetrics';

export const REFRESH_MS = 15_000;

const DEPLOYMENT_SORT: SortKeys<Deployment> = {
  name: { label: 'Name', value: (d) => d.name },
  ready: { label: 'Ready', value: (d) => (d.replicas ? d.readyReplicas / d.replicas : null) },
  state: { label: 'State', value: (d) => (d.paused ? 'paused' : 'active') },
};

const POD_SORT: SortKeys<Pod> = {
  name: { label: 'Name', value: (p) => p.name },
  status: { label: 'Status', value: (p) => p.status },
  ready: { label: 'Ready', value: (p) => (p.total ? p.ready / p.total : null) },
  restarts: { label: 'Restarts', value: (p) => p.restarts, desc: true },
  age: { label: 'Age', value: (p) => p.startTime ?? p.createdAt, desc: true },
  node: { label: 'Node', value: (p) => p.node },
  deployment: { label: 'Deployment', value: (p) => p.deployment },
};

export function PodStatusChip({ status }: { status: string }) {
  return (
    <Chip size="sm" className={`oph-tone nyk-tone-${podStatusTone(status)}`}>
      {status}
    </Chip>
  );
}

function lastTermination(p: Pod): string | null {
  const c = p.containers.find((x) => x.lastTermination);
  if (!c?.lastTermination) return null;
  const t = c.lastTermination;
  return `${c.name}: ${t.reason} (exit ${t.exitCode})${t.finishedAt ? ` at ${new Date(t.finishedAt).toLocaleString()}` : ''}`;
}

export function PodsPage({ login }: { login: HubLogin }) {
  const canWrite = hasScope(login.scopes, SCOPES.podsWrite);
  const [params, setParams] = useSearchParams();
  const env = params.get('env') ?? '';
  const navigate = useNavigate();
  const { envs, loading: envsLoading, error: envsError } = useClusterEnvironments();
  const current = envs.find((e) => e.id === env);

  const notice = useNotice();
  const [data, setData] = useState<PodList | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [auto, setAuto] = useState(true);
  const [action, setAction] = useState<PendingAction | null>(null);
  const [eventsFor, setEventsFor] = useState<Pod | null>(null);
  const [metricsFor, setMetricsFor] = useState<Pod | null>(null);
  const seq = useRef(0);

  const load = useCallback(async () => {
    if (!env) return;
    const id = ++seq.current;
    setLoading(true);
    try {
      const list = await podsApi.list(env);
      if (id !== seq.current) return;
      setData(list);
      setError(null);
    } catch (e) {
      if (id === seq.current) setError(errorMessage(e));
    } finally {
      if (id === seq.current) setLoading(false);
    }
  }, [env]);

  useEffect(() => {
    setData(null);
    setError(null);
    if (current) void load();
  }, [current, load]);

  useEffect(() => {
    if (!auto || !current) return;
    const tick = () => {
      if (typeof document === 'undefined' || !document.hidden) void load();
    };
    const id = setInterval(tick, REFRESH_MS);
    const onVisible = () => !document.hidden && void load();
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [auto, current, load]);

  // per namespace, by name unless a column is chosen
  const deploymentSort = useSort(null, DEPLOYMENT_SORT, useParam('dsort', ''));
  const podSort = useSort(null, POD_SORT, useParam('sort', ''));
  const deployments = byNamespace(data?.deployments ?? []).map(
    ([ns, list]) => [ns, deploymentSort.sortRows(list)] as const
  );
  const podGroups = byNamespace(data?.pods ?? []).map(([ns, list]) => [ns, podSort.sortRows(list)] as const);

  const done = (msg: string) => {
    notice.success(msg);
    void load();
  };

  return (
    <Page
      title="Pods"
      description="Deployments and pods of one environment's namespaces."
      actions={
        <>
          <EnvPicker envs={envs} value={env} onChange={(id) => setParams(id ? { env: id } : {})} />
          <Switch checked={auto} onChange={setAuto} label="Auto-refresh 15 s" />
          <Button
            variant="ghost"
            size="sm"
            icon={<RefreshCw size={14} />}
            disabled={!current}
            loading={loading && !!data}
            onClick={() => void load()}
          >
            Refresh
          </Button>
        </>
      }
    >
      {notice.element}
      <ErrorBox error={envsError} />
      {envsLoading && <Loading loading />}
      {!envsLoading && !env && (
        <EmptyState card title="Choose an environment" hint="Only environments with a cluster are listed." />
      )}
      {env && !envsLoading && envs.length > 0 && !current && (
        <ErrorBox error={`Unknown or disabled environment “${env}”.`} />
      )}
      {current && (
        <>
          <p className="nyk-card-intro" data-testid="fetched-at">
            {data ? `Last refreshed ${new Date(data.fetchedAt).toLocaleTimeString()}` : ' '}
            {current.clusterName ? ` · cluster ${current.clusterName}` : ''}
          </p>
          <ErrorBox error={error} />
          {!data && loading && <Loading loading />}
          {data && (
            <>
              <div className="nyk-card">
                <div className="nyk-card-head">
                  <h2>Deployments</h2>
                  <span className="nyk-ui-hint">{data.deployments.length}</span>
                  <SortSelect sort={deploymentSort} />
                </div>
                {!deployments.length && <EmptyState title="No deployments" />}
                {deployments.map(([ns, list]) => (
                  <section key={ns} className="oph-ns-group" aria-label={`Deployments in ${ns}`}>
                    <h3 className="oph-ns-title">{ns}</h3>
                    <TableScroller>
                      <table className="nyk-ui-table nyk-cards">
                        <thead>
                          <tr>
                            <SortTh sort={deploymentSort} by="name" />
                            <SortTh sort={deploymentSort} by="ready" />
                            <th>Images</th>
                            <SortTh sort={deploymentSort} by="state" />
                            {canWrite && <th aria-label="Actions" />}
                          </tr>
                        </thead>
                        <tbody>
                          {list.map((d) => (
                            <DeploymentRow
                              key={d.name}
                              d={d}
                              canWrite={canWrite}
                              onAction={(kind) =>
                                setAction({
                                  kind,
                                  environmentId: env,
                                  namespace: d.namespace,
                                  name: d.name,
                                  replicas: d.replicas,
                                })
                              }
                            />
                          ))}
                        </tbody>
                      </table>
                    </TableScroller>
                  </section>
                ))}
              </div>

              <div className="nyk-card">
                <div className="nyk-card-head">
                  <h2>Pods</h2>
                  <span className="nyk-ui-hint">{data.pods.length}</span>
                  <SortSelect sort={podSort} />
                </div>
                {!podGroups.length && <EmptyState title="No pods" />}
                {podGroups.map(([ns, list]) => (
                  <section key={ns} className="oph-ns-group" aria-label={`Pods in ${ns}`}>
                    <h3 className="oph-ns-title">{ns}</h3>
                    <TableScroller>
                      <table className="nyk-ui-table nyk-cards">
                        <thead>
                          <tr>
                            <SortTh sort={podSort} by="name" />
                            <SortTh sort={podSort} by="status" />
                            <SortTh sort={podSort} by="ready" />
                            <SortTh sort={podSort} by="restarts" />
                            <SortTh sort={podSort} by="age" />
                            <SortTh sort={podSort} by="node" />
                            <SortTh sort={podSort} by="deployment" />
                          </tr>
                        </thead>
                        {list.map((p) => {
                          const term = lastTermination(p);
                          return (
                            <tbody key={p.name} className="nyk-row-group" data-pod={p.name}>
                              <tr>
                                <td className="nyk-card-title nyk-mono">{p.name}</td>
                                <td data-label="Status">
                                  <PodStatusChip status={p.status} />
                                </td>
                                <td data-label="Ready" className="nyk-ui-num">
                                  {p.ready}/{p.total}
                                </td>
                                <td data-label="Restarts" className="nyk-ui-num">
                                  {p.restarts > 0 ? (
                                    term ? (
                                      <Tooltip text={term}>
                                        <span className="oph-restarts" tabIndex={0}>
                                          {p.restarts}
                                        </span>
                                      </Tooltip>
                                    ) : (
                                      <span className="oph-restarts">{p.restarts}</span>
                                    )
                                  ) : (
                                    0
                                  )}
                                </td>
                                <td data-label="Age" title={p.createdAt}>
                                  {formatAge(p.startTime ?? p.createdAt)}
                                </td>
                                <td
                                  data-label="Node"
                                  className="nyk-mono nyk-node"
                                  title={p.node || undefined}
                                >
                                  {p.node || '—'}
                                </td>
                                <td data-label="Deployment">{p.deployment ?? '—'}</td>
                              </tr>
                              <ActionsRow colSpan={7}>
                                <Link
                                  className="nyk-ui-btn nyk-ui-btn-ghost nyk-ui-btn-sm"
                                  to={podLogsPath(env, p)}
                                >
                                  Logs
                                </Link>
                                <Button size="sm" variant="ghost" onClick={() => setEventsFor(p)}>
                                  Events
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => setMetricsFor(p)}>
                                  Metrics
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => navigate(logsLink(env, p.namespace, p.name))}
                                >
                                  Open in Logs
                                </Button>
                                {canWrite && (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    data-danger="true"
                                    onClick={() =>
                                      setAction({
                                        kind: 'delete',
                                        environmentId: env,
                                        namespace: p.namespace,
                                        name: p.name,
                                      })
                                    }
                                  >
                                    Delete
                                  </Button>
                                )}
                              </ActionsRow>
                            </tbody>
                          );
                        })}
                      </table>
                    </TableScroller>
                  </section>
                ))}
              </div>
            </>
          )}
        </>
      )}
      {action && (
        <ActionDialog
          action={action}
          onClose={() => setAction(null)}
          onDone={(msg) => {
            setAction(null);
            done(msg);
          }}
        />
      )}
      {metricsFor && (
        <PodMetricsDialog environmentId={env} pod={metricsFor} onClose={() => setMetricsFor(null)} />
      )}
      {eventsFor && (
        <PodEventsDialog environmentId={env} pod={eventsFor} onClose={() => setEventsFor(null)} />
      )}
    </Page>
  );
}

function DeploymentRow({
  d,
  canWrite,
  onAction,
}: {
  d: Deployment;
  canWrite: boolean;
  onAction: (kind: 'restart' | 'scale' | 'pause' | 'resume') => void;
}) {
  const problems = deploymentProblems(d);
  const degraded = d.readyReplicas < d.replicas;
  return (
    <tr data-deployment={d.name}>
      <td className="nyk-card-title">
        <span className="nyk-inline">
          <strong>{d.name}</strong>
          {problems.length > 0 && (
            <Tooltip text={problems.join('\n')}>
              <span className="oph-warn-icon" tabIndex={0} aria-label={`Conditions: ${problems.join('; ')}`}>
                <AlertTriangle size={15} aria-hidden="true" />
              </span>
            </Tooltip>
          )}
        </span>
      </td>
      <td data-label="Ready" className={`nyk-ui-num${degraded ? ' oph-degraded' : ''}`}>
        {d.readyReplicas}/{d.replicas}
      </td>
      <td data-label="Images">
        <span className="oph-images">
          {d.images.map((i) => (
            <code key={i} title={i}>
              {shortImage(i)}
            </code>
          ))}
        </span>
      </td>
      <td data-label="State">
        {d.paused ? (
          <Chip size="sm" className="oph-tone nyk-tone-amber">
            paused{d.pausedReplicas != null ? ` (${d.pausedReplicas})` : ''}
          </Chip>
        ) : (
          <span className="oph-off">active</span>
        )}
      </td>
      {canWrite && (
        <td className="nyk-row-actions">
          <Button size="sm" variant="ghost" onClick={() => onAction('restart')}>
            Restart
          </Button>
          <Button size="sm" variant="ghost" onClick={() => onAction('scale')}>
            Scale
          </Button>
          {d.paused ? (
            <Button size="sm" variant="ghost" onClick={() => onAction('resume')}>
              Resume
            </Button>
          ) : (
            <Button size="sm" variant="ghost" onClick={() => onAction('pause')}>
              Pause
            </Button>
          )}
        </td>
      )}
    </tr>
  );
}
